import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '../classes/api-response';
import { Admin } from '../model/admin';
import { Customer } from '../model/customer';
import { Researcher } from '../model/researcher';
import { Logistic } from '../model/logistic';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map } from 'rxjs/operators';
import { Box } from "../model/box";
import { Company } from "../model/company";
import { Constants } from "../utils/constants";
import { BoxAndTestbed } from "../model/box-and-testbed";
import { Notification } from "../model/notification";
import { User } from '../model/user';
import { Tester } from '../model/tester';


@Injectable({
  providedIn: 'root'
})
export class UsersService {

  private readonly entities = Constants.entities;

  constructor(private http: HttpClient) {
  }


  getCommonUser(id: Number): Observable<User> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.commonUser}/${id}`)
      .pipe(map(value => value.data as User))
  }

  // region ADMIN

  public getAllAdmins(): Observable<Admin[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.admins}`)
      .pipe(map(value => value.data as Admin[]));
  }

  public getAdmin(id: number | string): Observable<Admin> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.admins}/${id}`)
      .pipe(map(value => value.data as Admin));
  }

  public createAdmin(admin: Admin): Observable<Admin> {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/${this.entities.admins}`, admin)
      .pipe(map(value => value.data as Admin));
  }

  public updateAdmin(admin: Admin): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.admins}/${admin.id}`, admin)
      .pipe(map(() => { }));
  }

  public deleteAdmin(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.admins}/${id}`)
      .pipe(map(() => { }));
  }


  // endregion


  // --------------------------------------------------------------------------------


  //region CUSTOMER

  public getAllCustomers(): Observable<Customer[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.customers}`)
      .pipe(map(value => value.data as Customer[]));
  }

  public getAllCustomersByCompany(companyId?: number): Observable<Company[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${companyId}/${this.entities.customers}`)
      .pipe(map(value => value.data as Company[]));
  }

  public getCustomer(customerId: number | string): Observable<Customer> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.customers}/${customerId}`)
      .pipe(map(value => value.data as Customer));
  }

  public createCustomer(customer: Customer): Observable<Customer> {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${customer.company?.id}/${this.entities.customers}`, customer)
      .pipe(map(value => value.data as Customer));

  }

  public updateCustomer(customer: Customer): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.customers}/${customer.id}`, customer)
      .pipe(map(() => { }));
  }

  public deleteCustomer(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.customers}/${id}`)
      .pipe(map(() => { }));
  }

  // endregion

  // --------------------------------------------------------------------------------


  //region RESEARCHERS

  public getAllResearchers(): Observable<Researcher[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.researchers}`)
      .pipe(map(value => value.data as Researcher[]));
  }

  public getAllResearchersByBox(box: Box): Observable<Researcher[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.boxes}/${box?.id}/${this.entities.researchers}`)
      .pipe(map(value => value.data as Researcher[]));
  }

  public getResearcher(researcherId?: number | string): Observable<Researcher> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.researchers}/${researcherId}`)
      .pipe(map(value => value.data as Researcher));
  }

  public createResearcher(researcher: Researcher): Observable<Researcher> {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/${this.entities.researchers}`, researcher)
      .pipe(map(value => value.data as Researcher));

  }

  public updateResearcher(researcher: Researcher): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.researchers}/${researcher.id}`, researcher)
      .pipe(map(() => { }));
  }

  public deleteResearcher(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.researchers}/${id}`)
      .pipe(map(() => { }));
  }

  public addResearchersToBox(researchers?: Researcher[], boxId?: number, contractId?: number): Observable<void> {
    return this.http
      .put<ApiResponse>(`${environment.apiUrl}/${this.entities.contracts}/${contractId}/${this.entities.boxes}/${boxId}/${this.entities.researchers}`, researchers)
      .pipe(map(() => { }));

  }

  public getResearcherBoxesAndTestbeds(researcher?: Researcher): Observable<BoxAndTestbed[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.researchers}/${researcher?.id}/${this.entities.boxes}-new`)
      .pipe(map(value => value.data as BoxAndTestbed[]));
  }

  public removeResearcherFromBox(boxId: number, researcherId: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.boxes}/${boxId}/${this.entities.researchers}/${researcherId}`)
      .pipe(map(() => { }));
  }

  // endregion

  // --------------------------------------------------------------------------------


  //region TESTERS

  public getAllTesters(): Observable<Tester[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.testers}`)
      .pipe(map(value => value.data as Tester[]));
  }

  public getAllTestersByCompany(companyId?: number): Observable<Tester[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${companyId}/${this.entities.testers}`)
      .pipe(map(value => value.data as Tester[]));
  }

  public getAllTestersByTestbed(testbedId?: number): Observable<Tester[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.testbeds}/${testbedId}/${this.entities.testers}`)
      .pipe(map(value => value.data as Tester[]));
  }

  public getTester(testerId: number | string): Observable<Tester> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.testers}/${testerId}`)
      .pipe(map(value => value.data as Tester));
  }

  public createTester(tester: Tester): Observable<Tester> {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${tester.company?.id}/${this.entities.testers}`, tester)
      .pipe(map(value => value.data as Tester));

  }

  public updateTester(tester: Tester): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.testers}/${tester.id}`, tester)
      .pipe(map(() => { }));
  }

  public deleteTester(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.testers}/${id}`)
      .pipe(map(() => { }));
  }

  public addTestersToTestbed(testers?: Tester[], testbedId?: number, contractId?: number): Observable<void> {
    return this.http
      .put<ApiResponse>(`${environment.apiUrl}/${this.entities.contracts}/${contractId}/${this.entities.testbeds}/${testbedId}/${this.entities.testers}`, testers)
      .pipe(map(() => { }));

  }

  public removeTesterFromTestbed(testbedId: number, testerId: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.testbeds}/${testbedId}/${this.entities.testers}/${testerId}`)
      .pipe(map(() => { }));
  }
  // end region

  // --------------------------------------------------------------------------------

  //region LOGISTICS

  public getAllLogistics(): Observable<Logistic[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.logistics}`)
      .pipe(map(value => value.data as Logistic[]));
  }

  public getLogistic(id: number | string): Observable<Logistic> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.logistics}/${id}`)
      .pipe(map(value => value.data as Logistic));
  }

  public createLogistic(logistic: Logistic): Observable<Logistic> {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/${this.entities.logistics}`, logistic)
      .pipe(map(value => value.data as Logistic));

  }

  public updateLogistic(logistic: Logistic): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.logistics}/${logistic.id}`, logistic)
      .pipe(map(() => { }));
  }

  public deleteLogistic(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.logistics}/${id}`)
      .pipe(map(() => { }));
  }

  //endregion


  //region NOTIFICATIONS
  public getNotifications(skipBatch: number, userId: number, userGroup: string): Observable<Notification[]> {

    const params = new HttpParams().append('skipBatch', skipBatch);

    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${userGroup}/${userId}/${this.entities.notifications}`, { params: params })
      .pipe(map(value => value.data as Notification[]));
  }

  public markNotificationsAsRead(userId?: number, userGroup?: string, notificationIds?: { ids: (number | undefined)[] }): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${userGroup}/${userId}/${this.entities.notifications}`, notificationIds)
      .pipe(map(() => { }));
  }

  //endregion

  public getNotificationPreferences(userId?: number) {

    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.users}/${userId}/${this.entities.preferences}`);

  }

  public markNotificationPreferences(userId?: number, preferences?:any): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.users}/${userId}/${this.entities.preferences}`, preferences)
      .pipe(map(() => { }));
  }


}
